import { PieChart as RechartsPie, ResponsiveContainer, Pie, Cell, Legend } from 'recharts';
import { c } from '@klv/shared/utils/charts';
import type { IPieChart } from './types';
import { designSystemColors } from '@klv/shared/styles/colors';
import { useWindowDimensions } from '@klv/hooks/useWindowDimensions';

const RADIAN = Math.PI / 180;
const DesktopLabel = (props: any) => {
  const { cx, cy, midAngle, outerRadius, percent, name, backgroundColor } = props;
  const textColor = backgroundColor === 'charcoal' ? designSystemColors.white : designSystemColors.charcoal;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius * 0.7) * cos;
  const sy = cy + (outerRadius * 0.7) * sin;
  const ex = cx + outerRadius * 1.6 * (cos >= 0 ? 1 : -1);
  const ey = sy;
  const tex = cos >= 0 ? ex - 100 : ex;
  const textAnchor = cos >= 0 ? 'end' : 'start';
  const textAlign = cos >= 0 ? 'right' : 'left';

  return (
    <g fontFamily='Sohne'>
      <path d={`M${sx},${sy}L${ex},${ey}`} stroke={textColor} fill='none' />
      <circle cx={sx} cy={sy} r={3} fill={textColor} stroke='none' />
      <text x={ex} fontSize='1.6em' y={ey} dy={-5} textAnchor={textAnchor} fill={textColor}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
      <foreignObject x={tex} y={ey} dy={18} width='100' height='2100'>
        <p style={{ textAlign, fontFamily: 'Sohne', color: textColor }}>{name}</p>
      </foreignObject>
    </g>
  );
};

const MobileLabel = (props: any) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text fontFamily='Sohne' x={x} y={y} fontSize='1em' fill='black' textAnchor={x > cx ? 'start' : 'end'} dominantBaseline='central'>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const PieChart = ({ data, headers, backgroundColor, color, isMultiple }: IPieChart) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 1366;
  const isMobile = width <= 576;
  const strokeColor = backgroundColor === 'charcoal' ? designSystemColors.charcoal : designSystemColors.white;
  const legendTextColor = backgroundColor === 'charcoal' ? designSystemColors.white : designSystemColors.charcoal;

  return (
    <ResponsiveContainer width='100%' height={!isMultiple && isDesktop ? 360 : 560}>
      <RechartsPie>
        <Pie
          data={data}
          dataKey={headers[1]}
          nameKey={headers[0]}
          cx='50%'
          cy='50%'
          labelLine={false}
          label={!isMobile ? <DesktopLabel backgroundColor={backgroundColor} /> : <MobileLabel />}
          startAngle={90}
          endAngle={360 + 90}
        >
          {data.map((entry: any, i: number) => (
            <Cell key={`cell-${i}-${entry[headers[1]]}`} fill={c[color][i]} stroke={strokeColor} strokeWidth={2} />
          ))}
        </Pie>
        <Legend
          height={150}
          align={!isMultiple && isDesktop ? 'right' : 'center'}
          verticalAlign={!isMultiple && isDesktop ? 'middle' : 'bottom'}
          layout='vertical'
          iconSize={20}
          iconType='square'
          formatter={(value: string, entry: any ) => {
            const percentage = `${(entry.payload.percent * 100).toFixed(0)}%`;
            const percentageValue = `${percentage} \u00a0 ${value}`;            
            return (
              <span style={{ color: legendTextColor, display: 'inline-block', margin: '0 10px', paddingTop: '2rem' }}>
                {percentageValue}
              </span>
            );
          }}
        />
      </RechartsPie>
    </ResponsiveContainer>
  );
};

export default PieChart;
